import {
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
  useReducer,
  useState,
} from 'react'
import { useField, FormikHelpers } from 'formik'
import { flatten } from 'lodash'
import clsx from 'clsx'
import { useModal } from '@ebay/nice-modal-react'
import {
  CloseCircleFilled,
  DownOutlined,
  SearchOutlined,
} from '@ant-design/icons'

import { Input, Tooltip, TemplateList } from '@pepper/ui'

import { ReactComponent as TooltipInfo } from 'assets/images/mini_tooltip.svg'
import { ReactComponent as TemplateIcon } from 'assets/images/template-icon.svg'
import { ReactComponent as PlusIcon } from 'assets/images/fi_plus.svg'
import { ReactComponent as EmptyIcon } from 'assets/images/empty_list.svg'

import styles from './_.module.css'

interface Props<T extends Record<string, unknown> = TemplateDetails> {
  name: string
  label?: string
  required?: boolean
  actions: FormikHelpers<any>
  defaultStaticValue: string | number
  miniTooltip?: string
  options?: T[]
  shouldShowViewTemplateButton?: boolean
  onViewItemClick?: (itemId: string) => void
  imgHeaderText?: string
  imgBottomText?: string
  getItemsList?: (
    itemsData: T[],
    type: string
  ) => {
    key: string
    list: {
      header: string
      subHeader?: string
      id: string | number
    }[]
  }
  itemNameKey?: string
  companyIdKey?: string
  viewItemButtonText?: string
  shouldShowAddItemButton?: boolean
  onChange?: (value: string) => void
}

let blurTimeout: NodeJS.Timeout

const getTemplatesList = <T extends Record<string, unknown> = TemplateDetails>(
  templatesData: T[],
  type: string
) => {
  return {
    key: type,
    list: templatesData?.map(item => {
      return {
        header: item?.template_name as string,
        subHeader: `${(item?.creator as { first_name: string })?.first_name} ${
          (item?.creator as { last_name: string })?.last_name
        }`,
        id: item?.id as string,
      }
    }),
  }
}

type TemplateDropdownState = {
  searchText: string
  placeholderText: string
  isDropdownOpen: boolean
  shouldShowPlaceholderText: boolean
}

type TemplateDropdownActions =
  | {
      type: 'UPDATE_SEARCH_TEXT'
      payload: { searchText: string }
    }
  | { type: 'OPEN_DROPDOWN' }
  | { type: 'CLOSE_DROPDOWN' }
  | { type: 'SELECT_DROPDOWN_ITEM'; payload: { searchText: string } }

const templateDropdownState = {
  searchText: '',
  placeholderText: '',
  isDropdownOpen: false,
  shouldShowPlaceholderText: false,
}

const templateDropdownReducer = (
  state: TemplateDropdownState,
  action: TemplateDropdownActions
) => {
  switch (action.type) {
    case 'UPDATE_SEARCH_TEXT': {
      return {
        ...state,
        searchText: action.payload.searchText,
        shouldShowPlaceholderText: false,
      }
    }

    case 'OPEN_DROPDOWN': {
      return {
        ...state,
        placeholderText: state.searchText,
        isDropdownOpen: true,
        shouldShowPlaceholderText: true,
      }
    }

    case 'CLOSE_DROPDOWN': {
      return {
        ...state,
        searchText: state.placeholderText,
        shouldShowPlaceholderText: false,
        isDropdownOpen: false,
      }
    }

    case 'SELECT_DROPDOWN_ITEM': {
      const selectedItemText = action.payload.searchText
      return {
        ...state,
        searchText: selectedItemText,
        placeholderText: selectedItemText,
      }
    }

    default: {
      return state
    }
  }
}

const CustomSelectSearch = function <
  T extends Record<string, unknown> = TemplateDetails
>({
  name,
  label,
  required,
  actions,
  miniTooltip,
  options,
  shouldShowViewTemplateButton,
  onViewItemClick,
  imgHeaderText,
  imgBottomText,
  getItemsList,
  itemNameKey = 'template_name',
  companyIdKey = 'company',
  shouldShowAddItemButton,
  viewItemButtonText,
  onChange,
}: Props<T>): JSX.Element {
  const inputRef: any = useRef<any>(null)
  const { show: showCreateNewTemplateModal } = useModal('create-new-template')
  const {
    show: showTemplateViewerModal,
    remove: hideTemplateViewerModal,
    visible,
  } = useModal('template-viewer')
  const [field, meta, helpers] = useField(name)
  const [
    { searchText, placeholderText, isDropdownOpen, shouldShowPlaceholderText },
    dispatch,
  ] = useReducer(templateDropdownReducer, templateDropdownState)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')

  const list = useMemo(() => {
    const filteredOptions =
      searchText && !shouldShowPlaceholderText
        ? options?.filter(data =>
            (data?.[itemNameKey] as string)
              .toLowerCase()
              ?.includes(searchText.toLowerCase())
          )
        : options
    const pepperTemplates =
      filteredOptions?.filter(data => !data?.[companyIdKey]) ?? []
    const companyTemplates =
      filteredOptions?.filter(data => data?.[companyIdKey]) ?? []
    return [
      (getItemsList ?? getTemplatesList)(
        companyTemplates,
        name === 'research_report_id'
          ? 'Content Research Report'
          : 'Team Templates'
      ),
      (getItemsList ?? getTemplatesList)(pepperTemplates, 'Pepper Templates'),
    ]
  }, [
    searchText,
    shouldShowPlaceholderText,
    options,
    getItemsList,
    itemNameKey,
    companyIdKey,
  ])

  useEffect(() => {
    if (field.value) {
      const selectedTemplateName = options?.find(
        data => data?.id === field.value
      )
      selectedTemplateName?.[itemNameKey] &&
        dispatch({
          type: 'SELECT_DROPDOWN_ITEM',
          payload: {
            searchText: selectedTemplateName?.[itemNameKey] as string,
          },
        })
    }
  }, [field.value, options, itemNameKey])

  useLayoutEffect(() => {
    const activeTemplate: any =
      document.getElementsByClassName('activeTemplate')[0]
    const templateList: any =
      document.getElementsByClassName('scrollableList')[0]
    if (templateList) {
      templateList.style.height = `calc(85vh - ${
        templateList.getBoundingClientRect().top
      }px)`
    }
    if (isDropdownOpen && activeTemplate && templateList) {
      // We want the selected item to be in the middle of the list ideally
      const top =
        activeTemplate.getBoundingClientRect().top -
        templateList.getBoundingClientRect().top -
        50
      templateList?.scrollBy({
        top,
        behavior: 'instant',
      })
    }
  }, [isDropdownOpen])

  const handleListItemClick = useCallback(
    (val, details) => {
      setSelectedTemplateId(val)
      helpers.setValue(val)
      details &&
        dispatch({
          type: 'SELECT_DROPDOWN_ITEM',
          payload: { searchText: details },
        })
      clearTimeout(blurTimeout)
      dispatch({ type: 'CLOSE_DROPDOWN' })
    },
    [helpers]
  )

  const onViewTemplateClick = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId)

      showTemplateViewerModal({ templateId, onSubmit: handleListItemClick })
    },
    [showTemplateViewerModal, handleListItemClick]
  )

  useEffect(() => {
    const allTemplatesList = flatten(
      list.map(groupedTemplates => groupedTemplates.list)
    )

    const isTemplateExists = allTemplatesList.some(
      template => template.id === selectedTemplateId
    )

    if (!isTemplateExists && !!selectedTemplateId && visible) {
      hideTemplateViewerModal()
    }
  }, [hideTemplateViewerModal, list, selectedTemplateId, visible])

  return (
    <div className={styles.searchInput}>
      <label>
        <span className={styles.inputLabelV2}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
          {miniTooltip && (
            <Tooltip
              placement="top"
              title={() => (
                <div
                  dangerouslySetInnerHTML={{
                    __html: miniTooltip,
                  }}
                />
              )}
            >
              <TooltipInfo className={styles.tooltipPos} />
            </Tooltip>
          )}
        </span>
      </label>
      <Input
        itemRef={inputRef}
        className={clsx(styles.inputAreaV2, {
          [styles.borderRed]: meta.touched && meta.error,
        })}
        value={shouldShowPlaceholderText ? '' : searchText}
        placeholder={shouldShowPlaceholderText ? placeholderText : ''}
        onFocus={() => {
          clearTimeout(blurTimeout)
          dispatch({ type: 'OPEN_DROPDOWN' })
        }}
        onBlur={() => {
          blurTimeout = setTimeout(() => {
            helpers.setTouched(true)
            const selectedTemplate = options?.find(data =>
              [searchText, placeholderText].includes(
                data?.[itemNameKey] as string
              )
            )
            if (!selectedTemplate) {
              dispatch({
                type: 'UPDATE_SEARCH_TEXT',
                payload: { searchText: '' },
              })
              helpers.setValue('')
            } else {
              helpers.setValue(selectedTemplate?.id)
            }
            dispatch({ type: 'CLOSE_DROPDOWN' })
          }, 500)
        }}
        onChange={e => {
          dispatch({
            type: 'UPDATE_SEARCH_TEXT',
            payload: { searchText: e.target.value },
          })
          onChange?.(e.target.value)
        }}
        suffix={
          !isDropdownOpen ? (
            <DownOutlined className={styles.templateSearchIcon} />
          ) : (
            <SearchOutlined className={styles.templateSearchIcon} />
          )
        }
      />
      {isDropdownOpen && (
        <div className={styles.templateListWrap}>
          <div className={styles.templateListOverlayWrap}>
            <TemplateList
              selectedItemId={field.value}
              list={list}
              submitButtonText="Create New Template"
              listImage={
                <TemplateIcon className={styles.templateImageInList} />
              }
              handleListItemClick={handleListItemClick}
              handleSubmitClick={showCreateNewTemplateModal}
              plusImage={<PlusIcon />}
              emptyItemImage={<EmptyIcon />}
              imgHeaderText={imgHeaderText ?? 'Create a new Template'}
              imgBottomText={
                imgBottomText ??
                'Click on “Create new template” to create a new template'
              }
              shouldShowViewItemButton={shouldShowViewTemplateButton}
              onViewItemClick={onViewItemClick ?? onViewTemplateClick}
              viewItemButtonText={viewItemButtonText}
              shouldShowAddItemButton={shouldShowAddItemButton}
            />
          </div>
        </div>
      )}
      {meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

export default CustomSelectSearch
